import { useNavigate, useParams } from 'react-router-dom';
import { db } from '@/lib/db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { ChevronLeft, Play, Info, BookOpen } from 'lucide-react';
import { Button, Card } from '@/components/ui';

export function DrillDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const drill = useLiveQuery(() => db.drills.get(id ?? '')) ?? null;

    if (!drill) return null;

    const categoryColors: Record<string, string> = {
        neutral: 'text-gold-500 bg-gold-500/10 border-gold-500/20',
        top: 'text-category-top bg-category-top/10 border-category-top/20',
        bottom: 'text-category-bottom bg-category-bottom/10 border-category-bottom/20',
    };

    return (
        <div className="min-h-screen pb-4">
            {/* Header / Back */}
            <div className="pt-6 px-4 flex items-center justify-between mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="w-11 h-11 rounded-[14px] bg-bg-800 border border-white/[0.05] flex items-center justify-center text-text-muted hover:text-gold-500 hover:border-gold-500/30 transition-all active:scale-95"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <div className={cn(
                    "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border",
                    categoryColors[drill.position]
                )}>
                    {drill.position}
                </div>
            </div>

            <div className="px-4 space-y-6">
                {/* Title Section */}
                <div className="animate-fade-in-up stagger-1">
                    <h1 className="text-3xl font-black text-text-primary leading-tight tracking-tight">{drill.name}</h1>
                    <p className="text-text-secondary mt-2 font-medium flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-gold-500/50" />
                        {drill.category}
                    </p>
                </div>

                {/* Video Placeholder */}
                <Card className="aspect-video bg-bg-900 border-white/[0.05] flex flex-col items-center justify-center text-text-muted group cursor-pointer animate-fade-in-up stagger-2">
                    <div className="w-16 h-16 rounded-full bg-gold-500/10 flex items-center justify-center text-gold-500 group-hover:scale-110 transition-transform shadow-[0_0_20px_rgba(212,175,55,0.1)]">
                        <Play className="w-8 h-8 fill-current translate-x-0.5" />
                    </div>
                    <p className="mt-4 text-[12px] font-bold tracking-widest uppercase">Watch Demonstration</p>
                </Card>

                {/* Description */}
                <div className="animate-fade-in-up stagger-3 space-y-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-1 h-4 bg-gold-500 rounded-full" />
                        <h2 className="text-lg font-bold text-text-primary uppercase tracking-wide">Instructions</h2>
                    </div>
                    <p className="text-text-secondary leading-relaxed font-medium">
                        {drill.description}
                    </p>
                </div>

                {/* Points of Emphasis */}
                <Card className="animate-fade-in-up stagger-4 bg-bg-800/40 border-gold-500/5">
                    <div className="flex items-center gap-3 mb-4">
                        <Info className="w-5 h-5 text-gold-500" />
                        <h3 className="text-text-primary">Points of Emphasis</h3>
                    </div>
                    <ul className="space-y-3">
                        {['Maintain heavy head pressure', 'Stay on your toes', 'Finish with chest to chest'].map((point, i) => (
                            <li key={i} className="flex gap-3 text-sm text-text-secondary">
                                <span className="text-gold-500 font-bold">•</span>
                                {point}
                            </li>
                        ))}
                    </ul>
                </Card>

                {/* Action */}
                <div className="pt-4 animate-fade-in-up stagger-5">
                    <Button onClick={() => navigate('/train', { state: { position: drill.position } })} size="lg" className="w-full h-14 text-lg">
                        TRAIN THIS TECHNIQUE
                    </Button>
                </div>
            </div>
        </div>
    );
}

import { cn } from '@/lib/utils';
